import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import Paths from '@/constants/Paths';
import router from '@/router';
import {
  CardTypesEnum,
  CounterAuthenticationMethodsEnum,
  EnumDto,
  IsicapiVersionEnum,
  PublicSystemConfigurationDto,
  SystemConfigurationDto,
  TariffCoreTypesEnum,
} from '@/api';
import { PaymentGatewayTypes } from '@/constants/PaymentGatewayTypes';
import { Currencies } from '@common/core/Currency';
import { MIGRATION_FLAGS } from '@T-system/common/src/api/constants';
import isDefined from '@common/utils/isDefined';

const _systemConfiguration: SystemConfigurationDto = {
  allowedOAuthProviders: [],
  id: '',
  allowSingleTickets: false,
  eshopMultiLocalizationEnabled: false,
  cardApplicationFilesUploadEnabled: false,
  cardNamesEnabled: false,
  virtualCardEWalletEnabled: false,
  eshopFooterManualEnabled: false,
  SMTPPermeabilityEnabled: false,
  bankCardAsCarrier: false,
  virtualCardAsCarrier: false,
  showTaxOnReceipt: false,
  ticketMinValidFrom: 'TODAY',
  haulierTokenKey: '',
  showSimpleCardApplication: false,
  allowFreeCardApplication: false,
  paymentGateway: '',
  EETLocalization: '',
  currency: '',
  eshopAllowedPaymentTypes: [],
  counterAllowedPaymentTypes: [],
  mapPhoneAllowedPaymentTypes: [],
  personalDataPrintedOnCard: [],
  allowedPortableCardsOnEshop: [],
  allowedPortableCardsOnCounter: [],
  allowCameraInCardApplication: false,
  allowShortTermTickets: false,
  defaultLanguage: '',
  migrationFlags: [],
  marketingConsentCardEnabled: false,
  addressStreetRequired: false,
  addressStreetNumberRequired: false,
  addressCityRequired: false,
  addressZipCodeRequired: false,
  ticketCancellationFinalizationEnabled: false,
  priceListUseActualDateInsteadValidFrom: false,
  eshopReclamationEnabled: false,
  synchronizationWithExternalEnabled: false,
  assignCardWithoutAgeRestriction: false,
  allowCustomerCardGroup: false,
  haulierCardWLAsCarrier: false,
  allowBankCardApplicationFromCounter: false,
  allowCloseEshopToastOnlyUsingButton: false,
  allowPersistEshopSideMenu: false,
  allowFirstCardApplicationWithDiscountProfileForFree: false,
  disableDeleteAccountWithActiveTicketsAndEP: false,
  allowUpdateCardValidity: false,
  allowSetDefaultProfileValidFromByCardValidFrom: false,
  allowCustomerCreationUnder15Years: false,
  allowChildCustomerRegistration: false,
  allowCustomerProfileApplication: false,
  allowRewardProgram: false,
  allowTicketCancellation: false,
  allowExpressCardProduction: false,
  allowINCards: false,
  allowWhitelists: false,
  allowPickupInPostOffice: false,
  allowPaperCards: false,
  allowCouponAndCardValidToOverlap: false,
  tariffCoreType: TariffCoreTypesEnum.ZONE,
  allowClearing: false,
  customerProfileLimit: 2,
  allowOffenses: true,
  allowThirdPartyTickets: false,
  allowFlexibleIsicVerification: false,
  allowCardRemoval: false,
  allowedSmartCardType: CardTypesEnum.SMART_CARD_2,
  allowEshopVariableFare: false,
  allowEshopCardApplicationOnlyOnce: false,
  tariffCoreDataUnitsCsv: false,
  allowActivateBlockCard: false,
  ISICAPIVersions: [IsicapiVersionEnum['3_7']],
  allowedEshopCardTypesApplication: [],
  allowedEshopAssignedCardTypesApplication: [],
  counterAuthenticationMethod: CounterAuthenticationMethodsEnum.USERNAME_PASSWORD,
  counterVfdDisplayEnabled: false,
  allowEmployeePersonalNumberOnCard: false,
  allowOnlyOneActiveCardAtAll: false,
  allowPersonalizeAnonymousCard: false,
  allowPaperCoupons: false,
  allowPrintDuplicateCoupon: false,
  allowEshopAssignHaulierCard: false,
  allowRelationalCustomerSetProfileTypeEdit: false,
  autoApproveApplicationsWithRegularProfile: false,
  regularProfileAlwaysDefaultInCardApplicationOnEshop: false,
  allowCompanyCard: false,
  counterRequirePasswordChangeAfterFirstLogin: false,
  allowDiscountProfileWhenAutoApproveRegularProfile: false,
  allowRegisterDifferentProviderSmartCard: false,
};

export interface SystemConfigurationPayload {
  redirect: boolean;
  systemConfiguration: SystemConfigurationDto | PublicSystemConfigurationDto;
}

export const useSystemConfigurationStore = defineStore('systemConfiguration', () => {
  const configuration = ref<SystemConfigurationDto>(_systemConfiguration);
  const initialized = ref(false);

  const setSystemConfiguration = (payload: SystemConfigurationPayload) => {
    configuration.value = { ...configuration.value, ...payload.systemConfiguration };
    initialized.value = true;

    if (payload.redirect) {
      const { redirectUrl, token } = router.currentRoute.value.query;
      if (redirectUrl) {
        const path = token ? `${redirectUrl.toString()}?token=${token}` : redirectUrl.toString();
        router.push(path);
      } else {
        router.push(Paths.USER_ACCOUNT);
      }
    }
  };

  const clearSystemConfiguration = () => {
    configuration.value = _systemConfiguration;
  };

  const isCardTypeAllowedPrint = (cardType: CardTypesEnum) =>
    personalDataPrintedOnCard.value?.some((allowedCard: EnumDto) => allowedCard.name === cardType);

  const isCardTypeAllowedApplication = (cardType: CardTypesEnum) =>
    allowedEshopCardTypesApplication.value?.some((allowedCard: EnumDto) => allowedCard.name === cardType);

  const isCardTypeAllowedAssignedApplication = (cardType: CardTypesEnum) =>
    allowedEshopAssignedCardTypesApplication.value?.some((allowedCard: EnumDto) => allowedCard.name === cardType);

  const allowedPaymentTypes = computed(() => configuration.value.eshopAllowedPaymentTypes);
  const personalDataPrintedOnCard = computed(() => configuration.value.personalDataPrintedOnCard);
  const currency = computed(() =>
    Object.values(Currencies).find(currency => currency.Code === configuration.value.currency),
  );
  const cardApplicationAttachmentAllowed = computed(() => configuration.value.cardApplicationFilesUploadEnabled);
  const cardNamesEnabled = computed(() => configuration.value.cardNamesEnabled);
  const marketingConsentEnabled = computed(() => configuration.value.marketingConsentCardEnabled);
  const isCsobPaymentGateway = computed(() => configuration.value.paymentGateway === PaymentGatewayTypes.CSOB);
  const isGpPaymentGateway = computed(() => configuration.value.paymentGateway === PaymentGatewayTypes.GP);
  const isVirtualCardEWalletAllowed = computed(() => configuration.value.virtualCardEWalletEnabled);
  const isAllowedFreeCardApplication = computed(() => configuration.value.allowFreeCardApplication);
  const isBankCardAsCarrierAllowed = computed(() => configuration.value.bankCardAsCarrier);
  const isVirtualCardAsCarrierAllowed = computed(() => configuration.value.virtualCardAsCarrier);
  const isTelshopMigrationAllowed = computed(
    () =>
      configuration.value.migrationFlags.length && configuration.value.migrationFlags.includes(MIGRATION_FLAGS.TELSHOP),
  );
  const customerCreationUnder15YearsAllowed = computed(() => configuration.value.allowCustomerCreationUnder15Years);
  const isSmartCardAllowed = computed(() => configuration.value.allowedSmartCardType === CardTypesEnum.SMART_CARD_2);
  const isSmartCardWithoutStructureAllowed = computed(
    () => configuration.value.allowedSmartCardType === CardTypesEnum.SMART_CARD_WITHOUT_STRUCTURE,
  );
  const isAnySmartCardAllowed = computed(() => isSmartCardAllowed.value || isSmartCardWithoutStructureAllowed.value);
  const isElectronicWalletEnabled = computed(
    () => isSmartCardAllowed.value || (isVirtualCardAsCarrierAllowed.value && isVirtualCardEWalletAllowed.value),
  );
  const isIsicAllowed = computed(() => isDefined(configuration.value.ISICAPIVersions));
  const isIsicV10Enabled = computed(
    () => configuration.value.ISICAPIVersions?.includes(IsicapiVersionEnum['1_0']) ?? false,
  );
  // Interaktivní kontrola čísla přes API běží jen na čistě v3.7 instanci; s v1.0 v konfiguraci se
  // číslo ověřuje až při vytvoření žádosti (backend, v1.0) — viz funkční specifikace ISIC flow.
  const isInteractiveIsicCheckEnabled = computed(
    () =>
      (configuration.value.ISICAPIVersions?.includes(IsicapiVersionEnum['3_7']) ?? false) && !isIsicV10Enabled.value,
  );
  const isSynchronizationWithExternalAllowed = computed(() => configuration.value.synchronizationWithExternalEnabled);
  const isAssignCardWithoutAgeRestrictionAllowed = computed(() => configuration.value.assignCardWithoutAgeRestriction);
  const isCloseToastUsingButtonAllowed = computed(() => configuration.value.allowCloseEshopToastOnlyUsingButton);
  const isPersistSideMenuAllowed = computed(() => configuration.value.allowPersistEshopSideMenu);
  const isDisableDeleteAccountAllowed = computed(() => configuration.value.disableDeleteAccountWithActiveTicketsAndEP);
  const isCustomerProfileApplicationAllowed = computed(() => configuration.value.allowCustomerProfileApplication);
  const childRegistrationAllowed = computed(() => configuration.value.allowChildCustomerRegistration);
  const allowedOAuthProviders = computed(() => configuration.value.allowedOAuthProviders);
  const externalCardsAllowed = computed(() => configuration.value.allowINCards);
  const ticketCancellationAllowed = computed(() => configuration.value.allowTicketCancellation);
  const allowWhitelists = computed(() => configuration.value.allowWhitelists);
  const allowPersonalDetailsEdit = computed(() => configuration.value.allowPersonalDetailsEdit);
  const allowPersonalDetailsOptional = computed(() => configuration.value.allowPersonalDetailsOptional);
  const allowOffenses = computed(() => configuration.value.allowOffenses);
  const isRelationalTariffCore = computed(() => configuration.value.tariffCoreType === TariffCoreTypesEnum.RELATIONAL);
  const isThirdPartyTicketAllowed = computed(() => isRelationalTariffCore.value);
  const allowThirdPartyTickets = computed(() => configuration.value.allowThirdPartyTickets);
  const allowFlexibleIsicVerification = computed(() => configuration.value.allowFlexibleIsicVerification);
  const allowedSmartCardType = computed(() => configuration.value.allowedSmartCardType);
  const customerProfileLimit = computed(() => configuration.value.customerProfileLimit);
  const ticketMinValidFrom = computed(() => configuration.value.ticketMinValidFrom);
  const allowEshopVariableFare = computed(() => configuration.value.allowEshopVariableFare);
  const allowEshopCardApplicationOnlyOnce = computed(() => configuration.value.allowEshopCardApplicationOnlyOnce);
  const allowedPortableCardsOnEshop = computed(() => configuration.value.allowedPortableCardsOnEshop);
  const allowedEshopCardTypesApplication = computed(() => configuration.value.allowedEshopCardTypesApplication);
  const allowDiscountProfileWhenAutoApproveRegularProfile = computed(
    () => configuration.value.allowDiscountProfileWhenAutoApproveRegularProfile,
  );
  const allowedEshopAssignedCardTypesApplication = computed(
    () => configuration.value.allowedEshopAssignedCardTypesApplication,
  );
  const allowCouponAndCardValidToOverlap = computed(() => configuration.value.allowCouponAndCardValidToOverlap);
  const allowCouponBeyondBankCardValidity = computed(() => configuration.value.allowCouponBeyondBankCardValidity);
  const isBankCardAllowed = computed(() => configuration.value.bankCardAsCarrier);
  const ISICAPIVersions = computed(() => configuration.value.ISICAPIVersions);
  const allowOnlyOneActiveCardAtAll = computed(() => configuration.value.allowOnlyOneActiveCardAtAll);
  const allowPersonalizeAnonymousCard = computed(() => configuration.value.allowPersonalizeAnonymousCard);
  const allowedAssignHaulierCard = computed(() => configuration.value.allowEshopAssignHaulierCard);
  const autoApproveApplicationsWithRegularProfile = computed(
    () => configuration.value.autoApproveApplicationsWithRegularProfile,
  );
  const regularProfileAlwaysDefaultInCardApplicationOnEshop = computed(
    () => configuration.value.regularProfileAlwaysDefaultInCardApplicationOnEshop,
  );

  const isAddressPersonalDataRequired = computed(
    () =>
      configuration.value.addressCityRequired &&
      configuration.value.addressStreetRequired &&
      configuration.value.addressStreetNumberRequired &&
      configuration.value.addressZipCodeRequired,
  );

  return {
    configuration,
    initialized,
    allowedPaymentTypes,
    personalDataPrintedOnCard,
    currency,
    cardApplicationAttachmentAllowed,
    allowDiscountProfileWhenAutoApproveRegularProfile,
    cardNamesEnabled,
    marketingConsentEnabled,
    isAddressPersonalDataRequired,
    isVirtualCardEWalletAllowed,
    isVirtualCardAsCarrierAllowed,
    isTelshopMigrationAllowed,
    customerCreationUnder15YearsAllowed,
    isIsicAllowed,
    isSynchronizationWithExternalAllowed,
    isPersistSideMenuAllowed,
    isAllowedFreeCardApplication,
    isCustomerProfileApplicationAllowed,
    isAnySmartCardAllowed,
    isElectronicWalletEnabled,
    isCloseToastUsingButtonAllowed,
    isAssignCardWithoutAgeRestrictionAllowed,
    isBankCardAsCarrierAllowed,
    isCsobPaymentGateway,
    isDisableDeleteAccountAllowed,
    isGpPaymentGateway,
    allowedOAuthProviders,
    childRegistrationAllowed,
    externalCardsAllowed,
    ticketCancellationAllowed,
    allowWhitelists,
    allowPersonalDetailsEdit,
    allowedPortableCardsOnEshop,
    allowPersonalDetailsOptional,
    allowOffenses,
    isThirdPartyTicketAllowed,
    allowThirdPartyTickets,
    allowFlexibleIsicVerification,
    allowedSmartCardType,
    customerProfileLimit,
    ticketMinValidFrom,
    allowEshopVariableFare,
    allowEshopCardApplicationOnlyOnce,
    isRelationalTariffCore,
    isSmartCardAllowed,
    isSmartCardWithoutStructureAllowed,
    isBankCardAllowed,
    allowedEshopCardTypesApplication,
    allowedEshopAssignedCardTypesApplication,
    allowCouponAndCardValidToOverlap,
    allowCouponBeyondBankCardValidity,
    ISICAPIVersions,
    isIsicV10Enabled,
    isInteractiveIsicCheckEnabled,
    allowOnlyOneActiveCardAtAll,
    allowPersonalizeAnonymousCard,
    allowedAssignHaulierCard,
    autoApproveApplicationsWithRegularProfile,
    regularProfileAlwaysDefaultInCardApplicationOnEshop,

    setSystemConfiguration,
    clearSystemConfiguration,
    isCardTypeAllowedPrint,
    isCardTypeAllowedApplication,
    isCardTypeAllowedAssignedApplication,
  };
});
